/**
 * Studio v2 — the artifact-type catalog (foundation-spec §4).
 *
 * Data-driven config (D13/D14), web-importable: the PICK page (`apps/web`) and
 * the skeleton injection (`apps/x-agent`) both import it directly. 12 types
 * across 3 goals — 9 shown + 3 buried (the website types, kept-but-buried D15).
 */
/** Helper: build a skeleton from a list of slot ids (label = Title Case of id). */
function slots(...ids) {
    return ids.map((slotId) => ({
        slotId,
        label: slotId
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
    }));
}
/** The 12 types, ordered for the PICK grid (§4.3). */
export const STUDIO_CATALOG = [
    // ── SELL (shown) ──
    {
        id: 'sales-deck',
        label: 'Sales deck',
        goal: 'SELL',
        format: 'slides',
        shape: 'paged',
        skeleton: slots('cover', 'problem', 'solution', 'proof', 'pricing', 'cta'),
        targetLength: { unit: 'slides', min: 8, max: 12 },
        recommendedThemeId: 'boardroom',
        buried: false,
        skillId: 'xavior-deck',
    },
    {
        id: 'proposal',
        label: 'Proposal',
        goal: 'SELL',
        format: 'doc',
        shape: 'single',
        skeleton: slots('summary', 'scope', 'approach', 'timeline', 'pricing', 'terms'),
        targetLength: { unit: 'sections', min: 4, max: 8 },
        recommendedThemeId: 'mono',
        buried: false,
        skillId: 'xavior-doc',
    },
    {
        id: 'case-study',
        label: 'Case study',
        goal: 'SELL',
        format: 'doc',
        shape: 'single',
        skeleton: slots('headline', 'challenge', 'approach', 'results', 'quote'),
        targetLength: { unit: 'sections', min: 3, max: 6 },
        recommendedThemeId: 'editorial',
        buried: false,
        skillId: 'xavior-doc',
    },
    // ── RAISE (shown) ──
    {
        id: 'investor-deck',
        label: 'Investor deck',
        goal: 'RAISE',
        format: 'slides',
        shape: 'paged',
        skeleton: slots('problem', 'solution', 'market', 'product', 'traction', 'business-model', 'team', 'ask'),
        targetLength: { unit: 'slides', min: 10, max: 10 },
        recommendedThemeId: 'boardroom',
        buried: false,
        skillId: 'xavior-deck',
    },
    {
        id: 'board-update',
        label: 'Board update',
        goal: 'RAISE',
        format: 'slides',
        shape: 'paged',
        skeleton: slots('cover', 'highlights', 'metrics', 'pipeline', 'risks', 'asks'),
        targetLength: { unit: 'slides', min: 6, max: 10 },
        recommendedThemeId: 'boardroom',
        buried: false,
        skillId: 'xavior-deck',
    },
    {
        id: 'one-pager',
        label: 'One-pager',
        goal: 'RAISE',
        format: 'doc',
        shape: 'single',
        skeleton: slots('headline', 'thesis', 'traction', 'team', 'ask'),
        targetLength: { unit: 'sections', min: 1, max: 1 },
        recommendedThemeId: 'mono',
        buried: false,
        skillId: 'xavior-doc',
    },
    // ── RELATE (shown) ──
    // Phase 2 (email generation): these two replace the former three kinds
    // (newsletter/announcement/check-in) — see docs/design/2026-07-03-studio-
    // email-generation-phase-2-design.md. Both build via the `xavior-email`
    // skill as raw, email-safe HTML (never the typed artifact skeleton); `grade`
    // is the deliverability fork the skill enforces.
    {
        id: 'email-campaign',
        label: 'Campaign email',
        goal: 'RELATE',
        format: 'email',
        shape: 'single',
        skeleton: slots('header', 'intro', 'stories', 'cta', 'footer'),
        targetLength: { unit: 'words', min: 150, max: 800 },
        recommendedThemeId: 'editorial',
        buried: false,
        skillId: 'xavior-email',
        grade: 'campaign',
    },
    {
        id: 'email-correspondence',
        label: 'Correspondence email',
        goal: 'RELATE',
        format: 'email',
        shape: 'single',
        skeleton: slots('greeting', 'body', 'sign-off'),
        targetLength: { unit: 'words', min: 60, max: 250 },
        recommendedThemeId: 'editorial',
        buried: false,
        skillId: 'xavior-email',
        grade: 'correspondence',
    },
    // ── Behind "See all" — buried: true (D15) ──
    {
        id: 'landing-page',
        label: 'Landing page',
        goal: 'SELL',
        format: 'web',
        shape: 'single',
        skeleton: slots('hero', 'value', 'features', 'proof', 'cta'),
        targetLength: { unit: 'sections', min: 4, max: 6 },
        recommendedThemeId: 'xavior',
        buried: true,
        skillId: 'xavior-webpage',
    },
    {
        id: 'link-in-bio',
        label: 'Link page',
        goal: 'RELATE',
        format: 'web',
        shape: 'single',
        skeleton: slots('header', 'links', 'footer'),
        targetLength: { unit: 'sections', min: 1, max: 1 },
        recommendedThemeId: 'xavior',
        buried: true,
        skillId: 'xavior-webpage',
    },
    {
        id: 'microsite',
        label: 'Microsite',
        goal: 'SELL',
        format: 'web',
        shape: 'paged',
        skeleton: slots('home', 'about', 'work', 'contact'),
        targetLength: { unit: 'sections', min: 2, max: 5 },
        recommendedThemeId: 'xavior',
        buried: true,
        skillId: 'xavior-webpage',
    },
];
/** Goal metadata for the 3 mockup groups (ordered). */
export const STUDIO_GOALS = {
    SELL: { label: 'Sell', order: 0 },
    RAISE: { label: 'Raise', order: 1 },
    RELATE: { label: 'Relate', order: 2 },
};
/**
 * The synthetic "+ Start from blank" entry — NOT in STUDIO_CATALOG (so it
 * doesn't count toward "12 types"), surfaced as a separate PICK affordance.
 */
export const BLANK_TYPE = {
    id: 'blank',
    label: 'Start from blank',
    goal: 'SELL',
    format: 'doc',
    shape: 'single',
    skeleton: [],
    targetLength: { unit: 'sections', min: 1, max: 1 },
    recommendedThemeId: 'xavior',
    buried: false,
    skillId: 'xavior-doc',
};
/** The 9 shown types (buried === false) — the PICK grid. */
export function visibleTypes() {
    return STUDIO_CATALOG.filter((t) => !t.buried);
}
/** All 12 types (the "See all" expansion). */
export function allTypes() {
    return [...STUDIO_CATALOG];
}
/** Look up a catalog type def by id (returns undefined for unknown / 'blank'). */
export function getArtifactType(id) {
    if (id === 'blank')
        return BLANK_TYPE;
    return STUDIO_CATALOG.find((t) => t.id === id);
}
//# sourceMappingURL=catalog.js.map